import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { Newspaper, ShieldCheck, CalendarDays, Megaphone, ArrowUpRight } from "lucide-react";

const RIBBON = [
  "Industry News",
  "Barn Maintenance",
  "Biosecurity Matters",
  "Flock Health",
  "Upcoming Events",
  "Producer Resources",
];

const UPDATES = [
  {
    icon: ShieldCheck,
    tag: "Biosecurity Matters",
    title: "Strong biosecurity starts with a clean barn",
    desc: "Thorough cleaning and disinfecting between flocks is one of the most effective ways to reduce disease pressure and protect your next production cycle.",
  },
  {
    icon: Newspaper,
    tag: "Industry News",
    title: "Why turnaround prep matters for Canadian producers",
    desc: "A properly prepared barn shortens downtime between cycles and supports flock performance from day one — a priority across Canada's poultry sector.",
  },
  {
    icon: CalendarDays,
    tag: "Barn Maintenance",
    title: "A seasonal cleaning checklist for poultry housing",
    desc: "Manure removal, wash-down, disinfecting, and equipment attention — a structured approach keeps facilities production-ready year-round.",
  },
];

export const IndustryInfo = () => {
  return (
    <section id="industry" data-testid="industry-section" className="bg-[#F9FAF8]">
      <div className="bg-[#DDE5D3] border-y border-[#C9D4C0] py-4 overflow-hidden" data-testid="industry-ribbon">
        <Marquee speed={40} gradient={false} pauseOnHover>
          {RIBBON.map((item) => (
            <span key={item} className="mx-12 inline-flex items-center gap-3 font-display font-bold uppercase tracking-[0.2em] text-sm text-[#3B4A2F]">
              <Megaphone size={15} className="text-[#1B4332]" />
              {item}
            </span>
          ))}
        </Marquee>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="max-w-2xl mb-14">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#1B4332] mb-4">Industry Information</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#081C15]" data-testid="industry-heading">
            Useful reading for poultry producers
          </h2>
          <p className="mt-4 text-base md:text-lg text-gray-700 leading-relaxed">
            Notes and updates on barn sanitation, biosecurity, and production readiness from the IMR team.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {UPDATES.map((u, i) => {
            const Icon = u.icon;
            return (
              <motion.article
                key={u.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: "easeOut" }}
                data-testid={`industry-card-${i}`}
                className="bg-white rounded-xl border border-[#D8E2DC] p-7 flex flex-col hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(27,67,50,0.08)] transition duration-300"
              >
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-lg bg-[#EAEFEA] flex items-center justify-center">
                    <Icon size={18} className="text-[#1B4332]" />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#1B4332]">{u.tag}</span>
                </div>
                <h3 className="mt-5 font-display font-bold text-lg text-[#081C15] leading-snug">{u.title}</h3>
                <p className="mt-3 text-sm text-gray-600 leading-relaxed flex-1">{u.desc}</p>
                <a
                  href="#contact"
                  data-testid={`industry-card-link-${i}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B4332] hover:text-[#081C15] transition-colors duration-200"
                >
                  Ask us about this
                  <ArrowUpRight size={15} />
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
